import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import PageBreadcrumbs from '@/components/PageBreadcrumbs';
import HeroSection from '@/components/layout/HeroSection';
import { Helmet } from 'react-helmet-async';
import { useMemo, useEffect } from 'react';
import { Calendar, Clock, ArrowLeft, FolderOpen, Grid } from 'lucide-react';
import { Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import StructuredData from '@/components/StructuredData';
import { getBlogPostingSchema } from '@/utils/seoSchemas';
import defaultHeroImage from '@/assets/hero-medical-cyber-main.jpg';

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const { data: post, isLoading, error } = useQuery({
    queryKey: ['blog-post', slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .eq('slug', slug)
        .eq('published', true)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!slug
  });

  // Load all categories for the category switcher
  const { data: categories = [] } = useQuery({
    queryKey: ['blog-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('category')
        .eq('published', true);
      
      if (error) throw error;
      
      const categoryCounts: Record<string, number> = data.reduce((acc, post) => {
        if (post.category) {
          acc[post.category] = (acc[post.category] || 0) + 1;
        }
        return acc;
      }, {} as Record<string, number>);
      
      return Object.entries(categoryCounts).map(([name, count]) => ({
        name,
        slug: name.toLowerCase().replace(/\s+/g, '-'),
        count,
      })).sort((a, b) => b.count - a.count);
    }
  });

  // Extract styles, fonts and body content from raw HTML
  const { extractedStyles, extractedFonts, bodyContent } = useMemo(() => {
    if (post?.content_mode !== 'raw' || !post.raw_html) {
      return { extractedStyles: '', extractedFonts: '', bodyContent: '' };
    }
    
    const html = post.raw_html;
    
    // Extract <style> tags
    const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
    const styles: string[] = [];
    let match;
    while ((match = styleRegex.exec(html)) !== null) {
      styles.push(match[1]);
    }
    
    // Extract <link> tags (fonts, etc.)
    const linkRegex = /<link[^>]*>/gi;
    const links: string[] = [];
    while ((match = linkRegex.exec(html)) !== null) {
      links.push(match[0]);
    }
    
    // Extract body content
    const bodyMatch = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
    const content = bodyMatch ? bodyMatch[1] : html;
    
  return {
    extractedStyles: styles.join('\n'),
    extractedFonts: links.join('\n'),
    bodyContent: content
  };
}, [post?.content_mode, post?.raw_html]);

  // Inject Manrope font + Lucide Icons for blog articles
  useEffect(() => {
    if (post?.content_mode === 'raw') {
      const fontLink = document.createElement('link');
      fontLink.href = 'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap';
      fontLink.rel = 'stylesheet';
      document.head.appendChild(fontLink);

      // Load Lucide Icons from CDN
      const lucideScript = document.createElement('script');
      lucideScript.src = 'https://unpkg.com/lucide@latest';
      lucideScript.async = true;
      lucideScript.onload = () => {
        // @ts-ignore
        if (window.lucide) {
          // @ts-ignore
          window.lucide.createIcons();
        }
      };
      document.body.appendChild(lucideScript);
      
      return () => {
        if (document.head.contains(fontLink)) {
          document.head.removeChild(fontLink);
        }
        if (document.body.contains(lucideScript)) {
          document.body.removeChild(lucideScript);
        }
      };
    }
  }, [post?.content_mode]);

// Inject styles dynamically into <head>
useEffect(() => {
  if (post?.content_mode === 'raw' && extractedStyles) {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = extractedStyles;
    styleElement.setAttribute('data-blog-post-styles', 'true');
    document.head.appendChild(styleElement);
    
    return () => {
      const existingStyle = document.head.querySelector('style[data-blog-post-styles]');
      if (existingStyle && document.head.contains(existingStyle)) {
        document.head.removeChild(existingStyle);
      }
    };
  }
}, [post?.content_mode, extractedStyles]);


  // Loading State
  if (isLoading) {
    return (
      <div className="min-h-screen relative bg-background">
        <Navigation />
        <div className="container mx-auto px-4 pt-16 pb-16">
          <p className="text-center text-muted-foreground">Lädt Artikel...</p>
        </div>
        <Footer />
      </div>
    );
  }

  // Error State
  if (error || !post) {
    return (
      <div className="min-h-screen relative bg-background">
        <Navigation />
        <div className="container mx-auto px-4 pt-16 pb-16 text-center">
          <h1 className="text-3xl font-bold mb-4">Artikel nicht gefunden</h1>
          <p className="text-muted-foreground mb-8">
            Der gesuchte Artikel existiert nicht oder ist nicht veröffentlicht.
          </p>
          <Button onClick={() => navigate('/blog')}>
            ← Zurück zum Blog
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  // Generate BlogPosting schema
  const blogPostingSchema = getBlogPostingSchema({
    title: post.title,
    excerpt: post.excerpt || '',
    slug: post.slug,
    created_at: post.created_at,
    updated_at: post.updated_at,
    featured_image: post.featured_image || post.og_image || undefined,
    content: post.content || post.raw_html || undefined
  });

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{post.meta_title || post.title} | Praxis Versichert</title>
        <meta name="description" content={post.meta_description || post.excerpt || ''} />
        <link rel="canonical" href={post.canonical_url || `https://praxis-versichert.de/blog/${post.slug}`} />
        {post.focus_keyword && <meta name="keywords" content={post.focus_keyword} />}
        {post.robots_meta && <meta name="robots" content={post.robots_meta} />}
        
        {/* Open Graph */}
        <meta property="og:title" content={post.meta_title || post.title} />
        <meta property="og:description" content={post.meta_description || post.excerpt || ''} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://praxis-versichert.de/blog/${post.slug}`} />
        {post.og_image && <meta property="og:image" content={post.og_image} />}
      </Helmet>

      <StructuredData data={blogPostingSchema} />

      <div className="min-h-screen bg-background">
        <Navigation />
        <PageBreadcrumbs items={[
          { label: "Home", path: "/" },
          { label: "Blog", path: "/blog" },
          { label: post.title }
        ]} />
        
        {/* Hero Header mit Featured Image Background - konsistent mit Homepage */}
        <HeroSection
          image={post.featured_image || defaultHeroImage}
          alt={`Header-Bild für: ${post.title}`}
          gradientPreset="fade-bottom"
          imageOpacity={40}
          overlayColor="background"
          className="min-h-[50vh] md:min-h-[55vh] lg:min-h-[60vh]"
        >
          <div className="container mx-auto px-4 py-16 md:py-20 lg:py-24 text-center flex flex-col items-center justify-center min-h-[50vh] md:min-h-[55vh] lg:min-h-[60vh]">
            {/* Back Button - Links positioniert */}
            <div className="absolute top-4 left-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/blog')}
                className="text-white/90 hover:text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück zum Blog
              </Button>
            </div>
            
            {/* Category Badge (optional - wenn vorhanden) */}
            {post.category && (
              <div className="mb-4 md:mb-6">
                <span className="inline-block px-4 py-2 bg-accent/20 border-2 border-accent rounded-full text-accent font-semibold text-sm md:text-base shadow-[0_0_20px_rgba(139,195,74,0.3)]">
                  {post.category}
                </span>
              </div>
            )}
            
            {/* Titel - Responsive Größen */}
            <h1 
              className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 md:mb-6 text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)] max-w-5xl"
              dangerouslySetInnerHTML={{ __html: post.title }}
            />
            
            {/* Meta Informationen */}
            <div className="flex flex-wrap items-center justify-center gap-4 text-white/90 text-sm md:text-base">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>
                  {new Date(post.created_at).toLocaleDateString('de-DE', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              </div>
            </div>
          </div>
        </HeroSection>
          
        <div className="container mx-auto px-4 py-12 md:py-16">
          {/* Content ohne Sidebar - volle Breite mit max-width */}
          <main className="max-w-4xl mx-auto">
            {/* Article Content */}
            {post.content_mode === 'raw' ? (
              // RAW HTML MODE: Render extracted body content with sanitization
              <div 
                style={{ isolation: 'isolate' }}
                className="blog-raw-content prose prose-lg dark:prose-invert max-w-none
                  [&_*]:!bg-transparent
                  prose-headings:text-foreground
                  prose-h1:text-4xl prose-h1:font-bold prose-h1:mb-6 [&>h1]:mt-8
                  prose-h2:text-3xl prose-h2:font-semibold prose-h2:mb-4 [&>h2]:mt-8
                  prose-h3:text-2xl prose-h3:font-medium prose-h3:mb-3 [&>h3]:mt-6
                  prose-p:text-lg prose-p:leading-relaxed prose-p:mb-4 prose-p:text-foreground
                  prose-a:text-accent hover:prose-a:text-accent/80 prose-a:no-underline hover:prose-a:underline
                  prose-strong:font-bold prose-strong:text-foreground
                  prose-ul:list-disc prose-ul:ml-6 prose-ul:space-y-2
                  prose-ol:list-decimal prose-ol:ml-6 prose-ol:space-y-2
                  prose-li:text-foreground prose-li:leading-relaxed
                  prose-img:rounded-lg prose-img:shadow-lg
                  prose-hr:border-border
                  prose-blockquote:border-l-4 prose-blockquote:border-accent
                  prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-muted-foreground
                  prose-code:text-accent prose-code:bg-muted prose-code:px-1 prose-code:rounded
                  prose-pre:bg-muted prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto
                  [&_div.space-y-6]:space-y-6"
                dangerouslySetInnerHTML={{ 
                  __html: DOMPurify.sanitize(bodyContent, {
                    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'code', 'pre', 'div', 'span', 'i', 'footer', 'header', 'article', 'section'],
                    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'target', 'rel', 'data-lucide', 'style'],
                    ALLOW_DATA_ATTR: true
                  })
                }}
              />
            ) : (
              // EDITOR MODE: Render editor content with sanitization
              <div 
                className="prose prose-lg dark:prose-invert max-w-none
                  prose-headings:text-foreground
                  prose-h1:text-4xl prose-h1:font-bold prose-h1:mb-6 prose-h1:mt-8
                  prose-h2:text-3xl prose-h2:font-semibold prose-h2:mb-4 prose-h2:mt-8
                  prose-h3:text-2xl prose-h3:font-medium prose-h3:mb-3 prose-h3:mt-6
                  prose-p:text-lg prose-p:leading-relaxed prose-p:mb-4 prose-p:text-foreground
                  prose-a:text-accent hover:prose-a:text-accent/80 prose-a:no-underline hover:prose-a:underline
                  prose-strong:font-bold prose-strong:text-foreground
                  prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6
                  prose-ol:my-4 prose-ol:list-decimal prose-ol:pl-6
                  prose-li:my-2 prose-li:text-foreground
                  prose-img:rounded-lg prose-img:border prose-img:my-6
                  prose-blockquote:border-l-4 prose-blockquote:border-accent 
                  prose-blockquote:pl-4 prose-blockquote:italic prose-blockquote:text-muted-foreground
                  prose-code:text-accent prose-code:bg-muted prose-code:px-1 prose-code:rounded
                  prose-pre:bg-muted prose-pre:p-4 prose-pre:rounded-lg prose-pre:overflow-x-auto"
                dangerouslySetInnerHTML={{ 
                  __html: DOMPurify.sanitize(post.content || '', {
                    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'code', 'pre', 'div', 'span', 'i', 'footer', 'header', 'article', 'section'],
                    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'target', 'rel', 'data-lucide', 'style'],
                    ALLOW_DATA_ATTR: true
                  })
                }}
              />
            )}

            {/* CTA Section */}
            {post.show_cta && (
              <div className="mt-16 p-8 border-2 border-primary/20 rounded-lg bg-card/50 backdrop-blur-sm">
                <h3 className="text-2xl font-bold mb-4 text-foreground">
                  Individuelle Beratung gewünscht?
                </h3>
                <p className="mb-6 text-muted-foreground text-lg">
                  Wir helfen dir bei der Auswahl der richtigen Cyberversicherung für deine Arztpraxis.
                </p>
                <Button 
                  variant="outline"
                  size="lg" 
                  onClick={() => navigate('/termin-buchen')}
                  className="font-semibold bg-transparent border border-white/20 text-white hover:bg-transparent hover:text-white hover:border-accent hover:shadow-[0_0_15px_hsl(var(--accent)/0.4)] transition-all duration-150"
                >
                  Jetzt Termin buchen
                </Button>
              </div>
            )}

            {/* Kategorie-Navigation nach CTA */}
            {categories.length > 0 && (
              <div className="mt-12 p-6 border-2 border-accent/30 rounded-xl bg-card/40 backdrop-blur-xl">
                <div className="flex items-center gap-3 mb-6">
                  <FolderOpen className="w-6 h-6 text-accent" />
                  <h3 className="text-2xl font-bold text-foreground">
                    Weitere Kategorien entdecken
                  </h3>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {categories.map((category) => (
                    <Link
                      key={category.slug}
                      to={`/blog?category=${category.slug}`}
                      className={`group relative p-4 rounded-lg border-2 transition-all duration-200 ${
                        post.category === category.name
                          ? 'border-accent/60 bg-accent/10 cursor-default'
                          : 'border-accent/20 bg-background/30 hover:border-accent/50 hover:bg-accent/5 hover:scale-[1.02]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-foreground group-hover:text-accent transition-colors">
                          {category.name}
                        </span>
                        <span className="text-xs px-2.5 py-1 rounded-full bg-accent/20 text-accent font-medium">
                          {category.count}
                        </span>
                      </div>
                      
                      {post.category === category.name && (
                        <div className="absolute top-2 right-2">
                          <span className="text-xs px-2 py-0.5 bg-accent text-background rounded-full font-bold">
                            Aktuell
                          </span>
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
                
                <div className="mt-6 pt-6 border-t border-accent/20 text-center">
                  <Link 
                    to="/blog/kategorien"
                    className="inline-flex items-center gap-2 text-accent hover:text-accent/80 font-semibold transition-colors"
                  >
                    <Grid className="w-4 h-4" />
                    Alle Kategorien in der Übersicht
                  </Link>
                </div>
              </div>
            )}
          </main>
        </div>

        <Footer />
      </div>
    </>
  );
}
